/**
 * Presentation helpers for LMS course cards.
 *
 * The backend is the source of truth for titles, but a card must never show a
 * raw UUID if metadata is missing, and it must never point at a dead external
 * placeholder service. These helpers are the single fallback policy used by the
 * student and university course lists.
 */

export const COURSE_FALLBACK_TITLE = 'Course details unavailable';

const COURSE_ID_KEYS = ['externalContentRootId', 'course_id', 'courseId', 'id', 'rootId'];
const COURSE_TITLE_KEYS = ['title', 'course_name', 'name'];
const COURSE_IMAGE_KEYS = ['thumbnailUrl', 'thumbnail_url', 'image_url', 'imageUrl', 'thumbnail'];

// Served from this app's own origin. The previous fallback pointed at
// via.placeholder.com, which no longer resolves; a data URI would also be
// blocked by the API's default-src CSP on some responses. A local asset is
// allowed by both CSP configurations and needs no network lookup.
export const COURSE_PLACEHOLDER_IMAGE = `${process.env.PUBLIC_URL || ''}/course-placeholder.svg`;

const normalize = (value) => String(value ?? '').trim();

const courseIdSet = (course) => {
  if (!course) return new Set();
  return new Set(
    COURSE_ID_KEYS.map((key) => normalize(course[key]).toLowerCase()).filter(Boolean)
  );
};

export const getCourseTitle = (course) => {
  if (!course) return COURSE_FALLBACK_TITLE;
  const ids = courseIdSet(course);
  for (const key of COURSE_TITLE_KEYS) {
    const value = normalize(course[key]);
    if (value && !ids.has(value.toLowerCase())) return value;
  }
  return COURSE_FALLBACK_TITLE;
};

const isUsableImageUrl = (value) => {
  const url = normalize(value);
  if (!url) return false;
  if (/^data:image\//i.test(url)) return true;
  if (!/^https?:\/\//i.test(url)) return false;
  try {
    const host = new URL(url).hostname.toLowerCase();
    // via.placeholder.com no longer resolves; treat it as missing instead of
    // rendering a broken image with the course title as alt text.
    if (host === 'placeholder.com' || host.endsWith('.placeholder.com')) return false;
  } catch (error) {
    return false;
  }
  return true;
};

export const getCourseImageUrl = (course) => {
  if (!course) return COURSE_PLACEHOLDER_IMAGE;
  for (const key of COURSE_IMAGE_KEYS) {
    if (isUsableImageUrl(course[key])) return normalize(course[key]);
  }
  return COURSE_PLACEHOLDER_IMAGE;
};

export const handleCourseImageError = (event) => {
  const image = event?.currentTarget;
  if (!image || image.dataset?.fallbackApplied === 'true') return;
  if (image.dataset) image.dataset.fallbackApplied = 'true';
  image.src = COURSE_PLACEHOLDER_IMAGE;
};
